import classnames from 'classnames'
import { STEP_STATE_DISABLED } from 'lib/stepper'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import NotFound from 'components/atoms/NotFound'
import PageLoading from 'components/atoms/PageLoading'
import PageSessionSelection from 'components/atoms/PageSessionSelection'
import PageTitle from 'components/atoms/PageTitle'
import Stepper from 'components/atoms/Stepper'
import {
  ASSIGNMENT_STEPS_LIST,
  ASSIGNMENT_STEP_CAP_MANAGEMENT,
  ASSIGNMENT_STEP_PUBLICATION,
  getAssignmentStepUrl,
  getLastAssignementStep,
} from 'components/organisms/Assignment/assignmentLib'
import useAssignmentStepNavigation from 'components/organisms/Assignment/useAssignmentNavigation'
import useAssignmentSession from 'components/organisms/Assignment/useAssignmentSession'
import useAssignmentSteps from 'components/organisms/Assignment/useAssignmentSteps'
import useCarsToAssign from 'components/organisms/Assignment/useCarsToAssign'
import AssignmentCapManagement from 'components/organisms/AssignmentCapManagement'
import AssignmentCarAssignment from 'components/organisms/AssignmentCarAssignment'
import AssignmentPublication from 'components/organisms/AssignmentPublication'

import useEntityData from 'hooks/useEntityData'
import useSession from 'hooks/useSession'
import useSessionsList from 'hooks/useSessionsList'

import {
  ROLES_ARVAL,
  ROLE_B2C,
  ROLE_LOGISTICS,
  ROLE_POST_SALES,
  ROLE_READ_ONLY,
  useAuth,
} from '../../auth'

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function AssignmentStepsRouter() {
  const { t } = useTranslation()

  const { id, step } = useParams()
  const [showHeader, setShowHeader] = useState(true)

  const [dateValue, setDateValue] = useState()

  const {
    isFetching: isFetchingSessions,
    fetchSessions,
    sessions,
  } = useSessionsList()

  const {
    isFetching: isFetchingSession,
    isUpdating: isUpdatingSession,
    fetchSession,
    updateAssignmentCap,
    session,
    error,
  } = useSession()

  const {
    isFetching: isFetchingCars,
    isUpdating: isUpdatingCars,
    fetchCarsToAssign,
    refetchCarsToAssign,
    cars,
    error: errorCarsToAssign,
  } = useCarsToAssign()

  const {
    isUpdating: isUpdatingAssignmentSession,
    result: assignmentStarted,
    assignmentEndedAt,
    startAssignment,
    confirmAssignment,
  } = useAssignmentSession()

  const {
    items: partnerItems,
    isFetching: isFetchingPartners,
    refetch: fetchPartners,
    setEntityState: setPartnersState,
  } = useEntityData({ limit: 9999 })

  const { fetchCurrentAssignmentSession, session: currentActiveSession } =
    useSession()

  const { handleStepValidation, getStepsWithState } = useAssignmentSteps()

  useEffect(() => {
    fetchSessions()
  }, [assignmentEndedAt, fetchSessions])

  useEffect(() => {
    fetchSession(id)
  }, [fetchSession, id, assignmentStarted, assignmentEndedAt])

  useEffect(() => {
    if (!sessions.length) {
      return
    }
    fetchCurrentAssignmentSession()
  }, [fetchCurrentAssignmentSession, sessions])

  useEffect(() => {
    if (!session?.sessionId || step === ASSIGNMENT_STEP_PUBLICATION) {
      return
    }

    fetchCarsToAssign(session?.sessionId)
  }, [fetchCarsToAssign, session?.sessionId, step])

  const refetchList = useCallback(() => {
    refetchCarsToAssign(session?.sessionId)
  }, [session, refetchCarsToAssign])

  useEffect(() => {
    if (!session?.sessionId) {
      return
    }

    fetchPartners(`sessions/${session.sessionId}/partners`)
  }, [fetchPartners, session?.sessionId, step])

  const partners = useMemo(() => {
    return partnerItems.filter((partner) => !!partner.groupId)
  }, [partnerItems])

  const { goToStep } = useAssignmentStepNavigation()

  const steps = useMemo(() => {
    return getStepsWithState(session, partners, cars)
  }, [cars, getStepsWithState, partners, session])

  const readOnly = useMemo(() => {
    return (
      isUpdatingAssignmentSession ||
      isFetchingCars ||
      isFetchingSessions ||
      isFetchingSession ||
      isUpdatingCars
    )
  }, [
    isUpdatingAssignmentSession,
    isFetchingCars,
    isFetchingSessions,
    isFetchingSession,
    isUpdatingCars,
  ])

  const handleStep = (index) => {
    goToStep(session.sessionId, index)
  }

  const handleStartAssignment = () => {
    startAssignment(session.sessionId)
  }

  const handleCapChange = (partnerId, value) => {
    const partner = partnerItems.find(
      (partner) => partner.partnerId === partnerId
    )
    if (partner) partner.cap = value || null

    updateAssignmentCap(session, partnerId, value || null, () =>
      setPartnersState((s) => ({ ...s, items: [...partnerItems] }))
    )
  }

  const { hasOneOfRoles } = useAuth()
  const canAccess = useMemo(() => hasOneOfRoles(ROLES_ARVAL), [hasOneOfRoles])
  const userRoleHasReadOnly = useMemo(
    () =>
      hasOneOfRoles([
        ROLE_READ_ONLY,
        ROLE_LOGISTICS,
        ROLE_B2C,
        ROLE_POST_SALES,
      ]),
    [hasOneOfRoles]
  )

  if (!canAccess) {
    return <Navigate to="/agenda" />
  }

  return (
    <div className={classnames('content-container', { details: !showHeader })}>
      {isFetchingSessions ||
      (isFetchingSession && !assignmentEndedAt) ||
      isFetchingCars ||
      (!Object.keys(session).length && !error) ||
      !Object.keys(steps).length ? (
        <PageLoading />
      ) : (error && Number(error.response.status) === 404) ||
        (errorCarsToAssign &&
          Number(errorCarsToAssign.response.status) === 404) ? (
        <NotFound />
      ) : !step ||
        !ASSIGNMENT_STEPS_LIST.includes(step) ||
        steps.find((s) => s.step === step).state === STEP_STATE_DISABLED ? (
        <Navigate to={getLastAssignementStep(session.sessionId, steps)} />
      ) : (
        <>
          {(showHeader || step !== ASSIGNMENT_STEPS_LIST[1]) && (
            <PageTitle
              label={t(`assignment.${step}.title`)}
              rightColumn={
                sessions.length && (
                  <PageSessionSelection
                    session={session}
                    currentActiveSession={currentActiveSession}
                    handleStepUrl={getAssignmentStepUrl}
                    sessionsList={sessions}
                    task="assignment"
                    readOnly={readOnly}
                  />
                )
              }
            />
          )}
          {step === ASSIGNMENT_STEPS_LIST[0] ? (
            <>
              <Stepper
                steps={steps}
                activeStep={0}
                handleStep={handleStep}
                stepperProps={{ sx: { maxWidth: '768px', m: 'auto' } }}
              />
              <AssignmentPublication
                data-testid="assignment-publication"
                session={session}
                partners={partners}
                handleStartAssignment={handleStartAssignment}
                handleValidation={() =>
                  handleStepValidation(step, session, partners, cars)
                }
                handleCapChange={handleCapChange}
                isFetchingPartners={isFetchingPartners}
                readOnly={readOnly}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          ) : step === ASSIGNMENT_STEPS_LIST[1] ? (
            <>
              {showHeader && (
                <Stepper
                  steps={steps}
                  activeStep={1}
                  handleStep={handleStep}
                  stepperProps={{ sx: { maxWidth: '768px', m: 'auto' } }}
                />
              )}
              <AssignmentCarAssignment
                data-testid="assignment-car-assignment"
                session={session}
                partners={partners}
                cars={cars}
                refetchList={refetchList}
                handleValidation={() =>
                  handleStepValidation(
                    ASSIGNMENT_STEP_CAP_MANAGEMENT,
                    session,
                    partners,
                    cars
                  )
                }
                handleConfirm={() =>
                  confirmAssignment(session?.sessionId, dateValue)
                }
                dateValue={dateValue}
                setDateValue={setDateValue}
                readOnly={readOnly}
                contentIsReadOnly={userRoleHasReadOnly}
                setShowHeader={setShowHeader}
              />
            </>
          ) : (
            <>
              <Stepper
                steps={steps}
                activeStep={2}
                handleStep={handleStep}
                stepperProps={{ sx: { maxWidth: '768px', m: 'auto' } }}
              />
              <AssignmentCapManagement
                data-testid="assignment-cap-assignment"
                session={session}
                partners={partners}
                cars={cars}
                handleCapChange={handleCapChange}
                handleValidation={() =>
                  handleStepValidation(step, session, partners, cars)
                }
                handleConfirm={() =>
                  confirmAssignment(session?.sessionId, dateValue)
                }
                dateValue={dateValue}
                setDateValue={setDateValue}
                isUpdatingSession={isUpdatingSession}
                readOnly={readOnly}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
